import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  Card,
  Box,
} from "@mui/material";
import {
  StyledButton,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledMultilineTextField,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { durationType } from "../initialData/initialData";
import { getHumanReadableDiffInPastDateWithAgo } from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";
import DurationAutocompleteField from "./DurationAutocompleteField";
import {
  normalizeLegacyDuration,
  parseCombinedDuration,
  durationUnitMap,
  formatDurationTextForDisplay,
} from "./durationUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";

const SurgicalHistory = ({
  surgicalHistoryData,
  setSurgicalHistoryData,
  dateForAppointment,
  activeHistorySection,
  setActiveHistorySection,
  startRecording,
  stopRecording,
  isAiEnabled,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.surgical,
  );
  console.log("Surgical History called", surgicalHistoryData);
  const today = dayjs(dateForAppointment);
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [autocompleteData, setAutoCompleteData] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [suggestApiCalled, setSuggestApiCalled] = useState(false);
  const [debounceFunctionCalledProcedure, setDebounceFunctionCalledProcedure] =
    useState(false);
  const [termSelectedOrAdded, setTermSelectedOrAdded] = useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();
  const [listeningOn, setListeningOn] = useState(false);
  const [clinicalNotesListening, setClinicalNotesListening] = useState(false);
  const [surgicalHistoryVars, setSurgicalHistoryVars] = useState({
    procedure: "",
    id: null,
    follow_up_description: "",
    recorded_date: null,
    clinical_note: "",
    duration: "",
  });
  const [procedureName, setProcedureName] = useState("");
  const [error, setError] = useState("");
  const [surgicalHistoryEditIndex, setSurgicalHistoryEditIndex] =
    useState(null);
  const hasPendingDurationSelection = (durationValue) =>
    /^\d+$/.test(String(durationValue || "").trim());
  const [mouseDownOnOption, setMouseDownOnOption] = useState(false);

  const debounceTimeout = useRef(null); // Use useRef to persist timeout
  const skipEffect = useRef(false);
  const optionChosen = useRef(false);

  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken"));
  }, [sessionStorage.getItem("accessToken")]);

  useEffect(() => {
    if (activeHistorySection == "Surgical History") {
      let timerId;
      // alert(`MH ${transcript}`);
      timerId = setTimeout(() => {
        setProcedureName(transcript);
      }, 3000);

      return () => {
        clearTimeout(timerId);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  useEffect(() => {
    if (skipEffect.current) {
      skipEffect.current = false; // reset after skipping
      return;
    }
    // Call the API with the debounced function when conditionName changes

    let timerOut = setTimeout(() => {
      if (procedureName.trim() !== "") {
        debouncedProcedureFilterCall(procedureName.toLowerCase());
      }
    }, 1000);
    return () => clearInterval(timerOut);
  }, [procedureName]);

  const startListening = (key) => {
    // alert("MH SL");
    if (transcript) {
      resetTranscript();
    }
    setActiveHistorySection("Surgical History");
    SpeechRecognition.startListening({ continuous: true, language: "en-IN" });
    setListeningOn(true);
    if (key == "procedure") {
      setClinicalNotesListening(false);
    } else {
      setClinicalNotesListening(true);
    }
  };
  console.log("surgicalHistoryVars", surgicalHistoryVars);
  const handleAddRow = async () => {
    if (
      fieldLayout.isFieldVisible("duration", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("duration", surgicalHistoryVars) &&
      !surgicalHistoryVars.duration
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "duration",
          "Duration is a mandatory field.",
        ),
      );
      return;
    } else if (hasPendingDurationSelection(surgicalHistoryVars.duration)) {
      setError("Please select a duration");
      return;
    }
    if (
      fieldLayout.isFieldVisible("clinical_note", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("clinical_note", surgicalHistoryVars) &&
      !surgicalHistoryVars.clinical_note?.trim()
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "clinical_note",
          "Clinical Notes is a mandatory field.",
        ),
      );
      return;
    }
    if (
      fieldLayout.isFieldVisible("follow_up_description", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("follow_up_description", surgicalHistoryVars) &&
      !surgicalHistoryVars.follow_up_description
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "follow_up_description",
          "Follow Up Description is a mandatory field.",
        ),
      );
      return;
    }
    console.log("Handle Add Called", surgicalHistoryVars);
    if (surgicalHistoryVars.procedure || procedureName.length > 0) {
      // Store the updated state in a local variable

      let newDetails,
        updatedFound = {},
        updatedSelectedOptiondetsils = {},
        foundData = false;

      if (!optionChosen.current) {
        const postBody = {
          term: procedureName,
          state: "active",
          semantictag: "procedure",
          acceptability: "synonyms",
          returnlimit: "-1",
          refsetid: null,
          parentid: null,
          excludeparentconcept: false,
          fullconcept: true,
        };

        try {
          if (procedureName.length > 2) {
            const response = await axios.get(
              `${REACT_APP_BACKEND_ROUTE_URL}search_medical_services/?name=${procedureName}`,
              // postBody,
              { headers: headers }
            );
            if (response) {
              console.log("Snomed Response", response);
              if (
                response.data.status === "Success" &&
                response.data.data.length > 0
              ) {
                foundData = response.data.data.find(
                  (d) => d.term === procedureName
                );
                console.log("Found Data", foundData);
                if (foundData) {
                  updatedFound = {
                    ...foundData,
                    found: true,
                    id: foundData.conceptId,
                  };
                } else {
                  updatedSelectedOptiondetsils = {
                    hierarchy: null,
                    isPreferredTerm: null,
                    conceptState: null,
                    conceptFsn: procedureName,
                    definitionStatus: null,
                    conceptId: null,
                    languageCode: "en",
                    typeId: null,
                    term: procedureName,
                    caseSignificanceId: "CASE_INSENSITIVE",
                    id: null,
                    effectiveTime: null,
                    activeStatus: null,
                    moduleId: null,
                  };
                }
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: procedureName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: procedureName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }
              if (foundData) {
                console.log("updatedFound", updatedFound);
                newDetails = {
                  ...surgicalHistoryVars,
                  procedure: updatedFound.term,
                  id: updatedFound.id,
                };
              } else {
                console.log(
                  "updatedSelectedOptiondetsils",
                  updatedSelectedOptiondetsils
                );
                newDetails = {
                  ...surgicalHistoryVars,
                  procedure: updatedSelectedOptiondetsils.term,
                  id: updatedSelectedOptiondetsils.id,
                };
              }
            } else {
              newDetails = {
                ...surgicalHistoryVars,
                procedure: procedureName,
                id: null,
              };
            }
          }
        } catch (err) {
          console.log(err);
          // toast.error(err);
          newDetails = {
            ...surgicalHistoryVars,
            procedure: procedureName,
            id: null,
          };
        }
      } else {
        newDetails = { ...surgicalHistoryVars };
      }

      const newRow = {
        procedure: newDetails.procedure,
        id: newDetails.id,
        follow_up_description: newDetails.follow_up_description,
        recorded_date: newDetails?.recorded_date?.format("YYYY-MM-DD"),
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.durationType
        ),
      };

      // Update family history rows by adding the new row

      const updatedRows = [...surgicalHistoryData["new"], newRow];

      setSurgicalHistoryData({
        ...surgicalHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setSurgicalHistoryVars({
        ...surgicalHistoryVars,
        procedure: "",
        id: null,
        follow_up_description: "",
        clinical_note: "",
        duration: "",
      });
      setProcedureName("");
      setError("");
      setDebounceFunctionCalledProcedure(false);
      setTermSelectedOrAdded(false);
      optionChosen.current = false;
      setFilteredOptions([]);
    } else {
      // Set error message if any field is missing
      setError("Procedure Name field is required");
    }
  };
  const handleSurgicalHistoryEditClick = (row, rowIndex) => {
    setProcedureName(row.procedure);
    setSurgicalHistoryVars({
      procedure: "",
      id: null,
      recorded_date: row.recorded_date ? dayjs(row.recorded_date) : null,
      follow_up_description: row.follow_up_description,
      clinical_note: row.clinical_note,
      duration: normalizeLegacyDuration(row?.duration, row?.duration, row?.durationType),
    });
    setSurgicalHistoryEditIndex(rowIndex);
  };
  const handleSurgicalHistoryUpdate = async () => {
    if (
      fieldLayout.isFieldVisible("duration", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("duration", surgicalHistoryVars) &&
      !surgicalHistoryVars.duration
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "duration",
          "Duration is a mandatory field.",
        ),
      );
      return;
    } else if (hasPendingDurationSelection(surgicalHistoryVars.duration)) {
      setError("Please select a duration");
      return;
    }
    if (
      fieldLayout.isFieldVisible("clinical_note", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("clinical_note", surgicalHistoryVars) &&
      !surgicalHistoryVars.clinical_note?.trim()
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "clinical_note",
          "Clinical Notes is a mandatory field.",
        ),
      );
      return;
    }
    if (
      fieldLayout.isFieldVisible("follow_up_description", surgicalHistoryVars) &&
      fieldLayout.isFieldRequired("follow_up_description", surgicalHistoryVars) &&
      !surgicalHistoryVars.follow_up_description
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "follow_up_description",
          "Follow Up Description is a mandatory field.",
        ),
      );
      return;
    }
    if (surgicalHistoryVars.procedure || procedureName.length > 0) {
      // Store the updated state in a local variable

      let newDetails,
        updatedFound = {},
        updatedSelectedOptiondetsils = {},
        foundData = false;

      if (!optionChosen.current) {
        const postBody = {
          term: procedureName,
          state: "active",
          semantictag: "procedure",
          acceptability: "synonyms",
          returnlimit: "-1",
          refsetid: null,
          parentid: null,
          excludeparentconcept: false,
          fullconcept: true,
        };

        try {
          if (procedureName.length > 2) {
            const response = await axios.get(
              `${REACT_APP_BACKEND_ROUTE_URL}search_medical_services/?name=${procedureName}`,
              // postBody,
              { headers: headers }
            );
            if (response) {
              console.log("Snomed Response", response);
              if (
                response.data.status === "Success" &&
                response.data.data.length > 0
              ) {
                foundData = response.data.data.find(
                  (d) => d.term === procedureName
                );
                console.log("Found Data", foundData);
                if (foundData) {
                  updatedFound = {
                    ...foundData,
                    found: true,
                    id: foundData.conceptId,
                  };
                } else {
                  updatedSelectedOptiondetsils = {
                    hierarchy: null,
                    isPreferredTerm: null,
                    conceptState: null,
                    conceptFsn: procedureName,
                    definitionStatus: null,
                    conceptId: null,
                    languageCode: "en",
                    typeId: null,
                    term: procedureName,
                    caseSignificanceId: "CASE_INSENSITIVE",
                    id: null,
                    effectiveTime: null,
                    activeStatus: null,
                    moduleId: null,
                  };
                }
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: procedureName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: procedureName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }
              if (foundData) {
                console.log("updatedFound", updatedFound);
                newDetails = {
                  ...surgicalHistoryVars,
                  procedure: updatedFound.term,
                  id: updatedFound.id,
                };
              } else {
                console.log(
                  "updatedSelectedOptiondetsils",
                  updatedSelectedOptiondetsils
                );
                newDetails = {
                  ...surgicalHistoryVars,
                  procedure: updatedSelectedOptiondetsils.term,
                  id: updatedSelectedOptiondetsils.id,
                };
              }
            } else {
              newDetails = {
                ...surgicalHistoryVars,
                procedure: procedureName,
                id: null,
              };
            }
          }
        } catch (err) {
          console.log(err);
          // toast.error(err);
          newDetails = {
            ...surgicalHistoryVars,
            procedure: procedureName,
            id: null,
          };
        }
      } else {
        newDetails = { ...surgicalHistoryVars };
      }

      const updatedRow = {
        procedure: newDetails.procedure,
        id: newDetails.id,
        follow_up_description: newDetails.follow_up_description,
        recorded_date: newDetails?.recorded_date?.format("YYYY-MM-DD"),
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.durationType
        ),
        source: undefined,
      };

      // Update family history rows by adding the new row

      const updatedSurgicalHistory = surgicalHistoryData["new"].map(
        (row, index) => {
          if (index === surgicalHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setSurgicalHistoryData({
        ...surgicalHistoryData,
        new: updatedSurgicalHistory,
      });
      // Reset family history variables and clear error
      setSurgicalHistoryVars({
        ...surgicalHistoryVars,
        procedure: "",
        id: null,
        follow_up_description: "",
        clinical_note: "",
        duration: "",
      });
      setProcedureName("");
      setError("");
      setSurgicalHistoryEditIndex(null);
      setDebounceFunctionCalledProcedure(false);
      setTermSelectedOrAdded(false);
      setFilteredOptions([]);
      optionChosen.current = false;
    } else {
      // Set error message if any field is missing
      setError("Procedure Name field is required");
    }
  };
  const handleSurgicalHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedSurgicalHistory = surgicalHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setSurgicalHistoryData({
      ...surgicalHistoryData,
      new: updatedSurgicalHistory,
    });
  };
  // const handleChangeSurgicalHistory = (field, value) => {
  //   let updatedDetails;
  //   if (field == "procedure") {
  //     setAutoCompleteData([]);
  //     setFilteredOptions([]);
  //     setTermSelectedOrAdded(true);
  //     updatedDetails = {
  //       ...surgicalHistoryVars,
  //       [field]: value.term,
  //       id: value.id,
  //     };
  //   }

  //   // If field is duration or durationType, calculate recorded_date using helper
  //   else if (
  //     (field === "duration" || field === "durationType") &&
  //     updatedDetails.duration &&
  //     updatedDetails.durationType
  //   ) {
  //     updatedDetails = { ...surgicalHistoryVars, [field]: value };
  //     const calculatedDate = calculateOnsetDate(
  //       parseInt(updatedDetails.duration),
  //       updatedDetails.durationType
  //     );

  //     updatedDetails.recorded_date = calculatedDate ?? null;
  //   } else if (field === "duration" || field === "durationType") {
  //     updatedDetails.recorded_date = null;
  //   } else {
  //     updatedDetails = {
  //       ...surgicalHistoryVars,
  //       [field]: value,
  //     };
  //   }

  //   setSurgicalHistoryVars(updatedDetails);
  // };
  const handleChangeSurgicalHistory = (field, value) => {
    let updatedDetails = {
      ...surgicalHistoryVars,
      [field]: field === "procedure" ? value.term : value,
    };

    if (field === "procedure") {
      setAutoCompleteData([]);
      setFilteredOptions([]);
      setTermSelectedOrAdded(true);
      updatedDetails.id = value.id;
    }

    if (field === "duration") {
      const { value: durationValue, unit: durationUnit } = parseCombinedDuration(
        updatedDetails.duration
      );
      if (durationValue && durationUnit) {
        const calculatedDate = calculateOnsetDate(parseInt(durationValue, 10), durationUnit);
        updatedDetails.recorded_date = calculatedDate ?? null;
      } else {
        updatedDetails.recorded_date = null;
      }
    }

    setSurgicalHistoryVars(updatedDetails);
  };

  const calculateOnsetDate = (duration, type) => {
    if (!duration) return null;
    const today = dayjs(dateForAppointment); // appointment date
    console.log("date onset", today.subtract(duration, durationUnitMap[type]));
    return today.subtract(duration, durationUnitMap[type]);
  };

  const handleClearProcedureName = () => {
    setError("");
    setProcedureName("");
    resetTranscript();
    setSurgicalHistoryVars({
      ...surgicalHistoryVars,
      procedure: "",
    });
    setTermSelectedOrAdded(false);
    setFilteredOptions([]);
    setDebounceFunctionCalledProcedure(false);
    // setMedicineList([])
  };
  const debouncedProcedureFilterCall = async (medicine) => {
    // console.log("Debounce function called");
    let semanticTagForConditionSearch = "procedure";
    const postBody = {
      term: medicine,
      state: "active",
      semantictag: semanticTagForConditionSearch,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (procedureName.length > 2) {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}search_medical_services/?name=${procedureName}`,
          // postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((medicineItem) => {
              return {
                term: medicineItem.term,
                id: medicineItem.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredOptions(filteredData);
          } else {
            try {
              if (procedureName.length > 2) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledProcedure(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  }; // Adjust the debounce duration (in milliseconds) as needed
  // const handleAddOption = async (term) => {
  //   const postBody = {
  //     term: term,
  //     state: "active",
  //     semantictag: "procedure",
  //     acceptability: "synonyms",
  //     returnlimit: "-1",
  //     refsetid: null,
  //     parentid: null,
  //     excludeparentconcept: false,
  //     fullconcept: true,
  //   };

  //   try {
  //     if (procedureName.length > 2) {
  //       const response = await axios.post(
  //         `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //         postBody,
  //         { headers: headers }
  //       );
  //       if (response) {
  //         let updatedFound = {},
  //           updatedSelectedOptiondetsils = {},
  //           foundData = false;
  //         console.log("Snomed Response", response);
  //         if (
  //           response.data.status === "Success" &&
  //           response.data.data.length > 0
  //         ) {
  //           foundData = response.data.data.find((d) => d.term === term);
  //           console.log("Found Data", foundData);
  //           if (foundData) {
  //             updatedFound = {
  //               ...foundData,
  //               found: true,
  //               id: foundData.conceptId,
  //             };
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: term,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: term,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }
  //         } else {
  //           updatedSelectedOptiondetsils = {
  //             hierarchy: null,
  //             isPreferredTerm: null,
  //             conceptState: null,
  //             conceptFsn: term,
  //             definitionStatus: null,
  //             conceptId: null,
  //             languageCode: "en",
  //             typeId: null,
  //             term: term,
  //             caseSignificanceId: "CASE_INSENSITIVE",
  //             id: null,
  //             effectiveTime: null,
  //             activeStatus: null,
  //             moduleId: null,
  //           };
  //         }

  //         setAutoCompleteData([]);
  //         setFilteredOptions([]);
  //         setSuggestApiCalled(false);
  //         setDebounceFunctionCalledProcedure(true);
  //         if (foundData) {
  //           handleChangeSurgicalHistory("procedure", updatedFound);
  //         } else {
  //           handleChangeSurgicalHistory(
  //             "procedure",
  //             updatedSelectedOptiondetsils
  //           );
  //         }
  //       }
  //     }
  //   } catch (err) {
  //     console.log(err);
  //     toast.error(err);
  //   }
  // };
  const handleClearClinicalNotes = () => {
    setError("");

    resetTranscript();
    setSurgicalHistoryVars({
      ...surgicalHistoryVars,
      clinical_note: "", // Use computed property name to set the field dynamically
    });
  };

  const handleConditionTyped = (value) => {
    setProcedureName(value);

    // if (debounceTimeout.current) {
    //   clearTimeout(debounceTimeout.current); // Clear previous timeout
    // }

    // debounceTimeout.current = setTimeout(() => {
    //   handleAddOption(value); // Only triggers after user stops typing
    // }, 500); // Adjust delay as needed
  };

  const renderSurgicalTableCell = (column, row, { isPrev = false, isNew = false } = {}) => {
    switch (column.key) {
      case "procedure":
        return isNew ? (
          <>
            {row?.procedure}
            {row?.source === "voice" && !row.id && (
              <Tooltip title="Machine-interpreted text — please verify before confirming">
                <span className="missing-id-warning">⚠️</span>
              </Tooltip>
            )}
          </>
        ) : (
          row?.procedure
        );
      case "clinical_note":
        return row?.clinical_note || "-";
      case "follow_up_description":
        return row?.follow_up_description || "-";
      case "duration":
        if (isPrev) {
          return row?.recorded_date
            ? formatDurationTextForDisplay(
              getHumanReadableDiffInPastDateWithAgo(row?.recorded_date, row)
            )
            : "-";
        }
        return (
          formatDurationTextForDisplay(
            normalizeLegacyDuration(row?.duration, row?.duration, row?.durationType)
          ) || "-"
        );
      default:
        return row?.[column.key] ?? "-";
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        {fieldLayout.isFieldVisible("procedure", surgicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("procedure", { xs: 11, sm: 11, md: 11 })} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            label={fieldLayout.getFieldLabel("procedure", "Procedure Name", surgicalHistoryVars)}
            variant="outlined"
            value={
              // surgicalHistoryVars.procedure !== ""
              //   ? surgicalHistoryVars.procedure
              //   :
              procedureName
            }
            onChange={(e) => {
              // if (!surgicalHistoryVars.procedure) {
              //   setProcedureName(e.target.value);
              // }
              handleConditionTyped(e.target.value);
            }}
            fullWidth
            onBlur={() => {
              setTimeout(() => {
                const shouldClear = !mouseDownOnOption;

                if (shouldClear) {
                  // setIsFocused(false);
                  setFilteredOptions([]);
                  setDebounceFunctionCalledProcedure(false);
                  setTermSelectedOrAdded(false);
                }

                setMouseDownOnOption(false);
              }, 100);
            }}
            onFocus={() => {
              // setIsFocused(true);
              if (procedureName.trim() !== "") {
                debouncedProcedureFilterCall(procedureName.toLowerCase()); // re-trigger filtering
              }
            }}
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearProcedureName}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                  {/* {!termSelectedOrAdded && (
                    <Tooltip title="Add" style={{ marginRight: "5px" }}>
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleAddOption}
                      >
                        <AddIcon />
                      </OutlinedIconFab>
                    </Tooltip>
                  )} */}
                  {/* <>
                    {listeningOn === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={startListening("procedure")}
                          variant="contained"
                          color="defaultColor"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Stop Listening">
                        <OutlinedIconFab
                          onClick={() => {
                            SpeechRecognition.stopListening();
                            setListeningOn(false);
                          }}
                          variant="outlined"
                          color="default"
                          size="small"
                        >
                          <KeyboardVoiceIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    )}
                  </> */}
                  <>
                    {isAiEnabled &&
                      (listeningOn === false ? (
                        <Tooltip title="Start Listening">
                          <OutlinedIconFab
                            // onClick={() => startListening("procedure")}
                            onClick={() => {
                              startRecording();
                              setListeningOn(true);
                            }}
                            variant="contained"
                            color="defaultColor"
                            size="small"
                          >
                            <Icon name="micNotInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ) : listeningOn === true &&
                        clinicalNotesListening === true ? (
                        <Tooltip title="Start Listening">
                          <OutlinedIconFab
                            // onClick={() => startListening("procedure")}
                            onClick={() => {
                              startRecording();
                              setListeningOn(true);
                            }}
                            variant="contained"
                            color="default"
                            size="small"
                          >
                            <Icon name="micNotInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Stop Listening">
                          <OutlinedIconFab
                            onClick={() => {
                              // SpeechRecognition.stopListening();
                              stopRecording("Surgeries");
                              setListeningOn(false);
                            }}
                            variant="outlined"
                            color="default"
                            size="small"
                          >
                            <Icon name="micInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ))}
                  </>
                </div>
              ),
            }}
          />
          {filteredOptions.length > 0 ? (
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "10px",
                padding: "20px",
                maxHeight: "200px", // Set your desired maximum height here
                overflowY: "auto", // Add vertical scrolling
              }}
            >
              {/* Display filtered medicines */}
              {suggestApiCalled && (
                <div style={{ color: "red" }}>
                  No Exact Matches found ! You can search for the following
                  Suggestions.
                </div>
              )}

              <>
                {filteredOptions.map((op) => (
                  <div key={op}>
                    {
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {op.added ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <StyledButton
                              color="success"
                              sx={{
                                textAlign: "left",
                                justifyContent: "flex-start",
                                width: "100%",
                              }}
                            >
                              {op.term}
                            </StyledButton>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                flexDirection: "row",
                                color: "green",
                                fontStyle: "bold",
                              }}
                            >
                              <span>&#10003;</span>
                              <span>(Added)</span>
                            </div>
                          </div>
                        ) : (
                          <StyledButton
                            color="grey"
                            sx={{
                              color: "#000",
                              textAlign: "left",
                              justifyContent: "flex-start",
                              width: "100%",
                            }}
                            onClick={() => {
                              skipEffect.current = true; // prevent debounce call
                              optionChosen.current = true;
                              setProcedureName(op.term);
                              handleChangeSurgicalHistory("procedure", op);
                            }}
                            onMouseDown={() => setMouseDownOnOption(true)}
                          >
                            {op.term}
                          </StyledButton>
                        )}
                      </div>
                    }
                  </div>
                ))}
              </>
            </div>
          ) : filteredOptions.length == 0 &&
            debounceFunctionCalledProcedure &&
            !termSelectedOrAdded ? (
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "10px",
                padding: "20px",
                maxHeight: "300px", // Set your desired maximum height here
                overflowY: "auto", // Add vertical scrolling
              }}
            >
              <div style={{ color: "red" }}>No Options Found!</div>
            </div>
          ) : null}
        </Grid>
        )}
        {fieldLayout.isFieldVisible("clinical_note", surgicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("clinical_note", { xs: 11, md: 11, sm: 11 })}>
          <StyledTextField
            variant="outlined"
            label={fieldLayout.getFieldLabel("clinical_note", "Clinical Notes", surgicalHistoryVars)}
            // multiline
            // rows={2}
            value={surgicalHistoryVars.clinical_note}
            onChange={(e) =>
              handleChangeSurgicalHistory("clinical_note", e.target.value)
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearClinicalNotes}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>

                  <>
                    {/* {listeningOn === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="defaultColor"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : listeningOn === true &&
                      clinicalNotesListening === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="default"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Stop Listening">
                        <OutlinedIconFab
                          onClick={() => {
                            SpeechRecognition.stopListening();
                            setListeningOn(false);
                            setClinicalNotesListening(false);
                          }}
                          variant="outlined"
                          color="default"
                          size="small"
                        >
                          <KeyboardVoiceIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    )} */}
                  </>
                </div>
              ),
            }}
          />
        </Grid>
        )}
        {fieldLayout.isFieldVisible("follow_up_description", surgicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("follow_up_description", { xs: 11, md: 11, sm: 11 })}>
          <StyledTextField
            variant="outlined"
            label={fieldLayout.getFieldLabel("follow_up_description", "Follow Up Description", surgicalHistoryVars)}
            // multiline
            // rows={2}
            value={surgicalHistoryVars.follow_up_description}
            onChange={(e) =>
              handleChangeSurgicalHistory(
                "follow_up_description",
                e.target.value
              )
            }
            fullWidth
          />
        </Grid>
        )}

        {fieldLayout.isFieldVisible("duration", surgicalHistoryVars) && (
        <Grid
          item
          {...fieldLayout.getFieldGrid("duration", { xs: 11, sm: 11, md: 8 })}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{ flexShrink: 0, whiteSpace: { sm: "nowrap" } }}
          >
            {fieldLayout.getFieldLabel("duration", "Time Since Surgery:", surgicalHistoryVars)}
          </Typography>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 260, md: 280 } }}>
            <DurationAutocompleteField
              label="Duration"
              value={surgicalHistoryVars.duration || ""}
              onChange={(combined) =>
                handleChangeSurgicalHistory("duration", combined)
              }
              durationType={durationType}
            />
          </Box>
        </Grid>
        )}

        <Grid item xs={11} sm={4} md={4} sx={{ display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Recorded Date"
                onChange={(date) =>
                  handleChangeSurgicalHistory("recorded_date", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  surgicalHistoryVars?.recorded_date
                    ? dayjs(surgicalHistoryVars?.recorded_date)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={11} sm={11} md={3} sx={{ display: "flex" }}>
          <StyledButton
            bgColor="#0E3F66"
            onClick={
              surgicalHistoryEditIndex !== null
                ? handleSurgicalHistoryUpdate
                : handleAddRow
            }
            variant="contained"
            sx={{ width: { xs: "100%" }, marginLeft: { md: "auto" } }}
            color="defaultColor"
          >
            {surgicalHistoryEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </Grid>
        <Grid item xs={11} sm={11} md={11}>
          {surgicalHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    {fieldLayout.tableColumns.map((column) => (
                      <StyledTableCellHistory key={column.key}>
                        {column.label}
                      </StyledTableCellHistory>
                    ))}
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {surgicalHistoryData["prev"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory key={column.key}>
                          {renderSurgicalTableCell(column, row, { isPrev: true })}
                        </StyledTableCellHistory>
                      ))}
                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11} md={11}>
          {surgicalHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    {fieldLayout.tableColumns.map((column) => (
                      <StyledTableCellHistory key={column.key}>
                        {column.label}
                      </StyledTableCellHistory>
                    ))}
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {surgicalHistoryData["new"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory key={column.key}>
                          {renderSurgicalTableCell(column, row, { isNew: true })}
                        </StyledTableCellHistory>
                      ))}
                      <StyledTableCellHistory>
                        {surgicalHistoryData["prev"].includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleSurgicalHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleSurgicalHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default SurgicalHistory;
